class DivideBuy extends HTMLElement {
    constructor() {
        super();
        this.price = Number(this.dataset.price);
        this.currencySymbol = (window.divide_buy?.currency_symbol?.length > 0) ? window.divide_buy.currency_symbol : window.divide_buy?.currency;
        this.instalments = this.getInstalments();
        this.minimumOrderAmount = Math.min(...this.instalments.map(data => data.value));
        this.softCreditModalPopup = document.querySelector('[data-asset="divide-buy-soft-credit-modal-popup"]');
        this.tooltipPopup = this.querySelector('[data-asset="tooltip-wrapper"]');
        this.tooltipPopupToggleButton = this.querySelector('[data-asset="tooltip-toggle-button"]');
    }

    connectedCallback() {
        if (this.price < this.minimumOrderAmount) {
            this.style.display = "none";
        } else {
            this._initEvents();
            const loanAmount = this.price;
            const installments_details = [];
            this.instalments.forEach(instalment => {
                const minInstalmetAmount = Number(instalment.min_value);
                const maxInstalmetAmount = Number(instalment.max_value);
                if (loanAmount > minInstalmetAmount && loanAmount < maxInstalmetAmount) {
                    const calculatedInstalment = this.calculateInstalmentAmountsForLoan(loanAmount, (Number(instalment.interest_rate) / 100), Number(instalment.key));
                    installments_details.push(calculatedInstalment);
                }
            });
            
            let tooltipInstalmentsHtml = '';
            installments_details.forEach(instalment => {
                tooltipInstalmentsHtml += `<div class="instalment">
                    <div class="installment-month">${instalment.numberOfMonths} <span>payments of </span></div>
                    <div class="installment-amount"> ${this.currencySymbol}${instalment.regularMonthlyInstalment.toFixed(2)}</div>
                </div>`;
            })
            tooltipInstalmentsHtml += `<div class="instalment-summary">
                <div class="interest">Interest: <span>${this.currencySymbol}0.00</span></div>
                <div class="total">Total: <span>${this.currencySymbol}${this.price}</span></div>
            </div>`;
            this.querySelector('[data-asset="monthly-instalments"]').innerHTML = tooltipInstalmentsHtml;
            this.querySelector('[data-asset="divide-text"]').textContent = `Or from ${this.currencySymbol}${installments_details[installments_details.length - 1]?.regularMonthlyInstalment.toFixed(2)} p/m, ${installments_details[installments_details.length - 1]?.interestRate?.toFixed(2)}% credit with`;

            const isRetailerHasInterestBearingInstalment = this.instalments.some(item => Number(item.is_ibc) === 1);
            if (isRetailerHasInterestBearingInstalment) {
                this.querySelector('[data-asset="apr-representative-text"]').innerHTML = this.getRepresentativeAprExampleHtml();
            } else {
                this.querySelector('[data-asset="apr-representative-text"]').innerHTML = '0% APR representative example. <br>Credit subject to status and minimum order value.';
            }
        }
    }

    _initEvents() {
        const openModalButton = this.querySelector('[data-asset="model-open-button"]');
        openModalButton?.addEventListener('click', () => this.handleSoftSearchModalOpen());

        const closeModalButton = this.softCreditModalPopup?.querySelector('[data-asset="modal-popup-close"]');
        closeModalButton?.addEventListener('click', () => this.handleSoftSearchModalClose());

        this.tooltipPopupToggleButton?.addEventListener('click', () => this.handleTooltipModalToggle());

        const closeTooltipModal = this.querySelector('[data-asset="tooltip-close-button"]');
        closeTooltipModal?.addEventListener('click', () => this.handleTooltipModalClose());

        document.addEventListener('scroll', this.handleScrollOrResize);
        document.addEventListener('resize', this.handleScrollOrResize);
    }

    async fetchSplashKey() {
        if (!window.divide_buy.soft_search_api_url) {
            return null;
        }

        try {
            const response = await fetch(window.divide_buy.soft_search_api_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    retailer_base_url: `https://${window.divide_buy.retailer_domain}`,
                    order_total: this.price,
                }),
            });

            if (!response.ok) {
                console.error('Failed to fetch splash key:', response.status, response.statusText);
                return null;
            }

            const data = await response.json();
            return data?.splashKey || null;
        } catch (error) {
            console.error('Error fetching splash key:', error);
            return null;
        }
    }


    async handleSoftSearchModalOpen() {
        this.softCreditModalPopup?.classList.add('active');
        const splashKey = await this.fetchSplashKey();
        if (splashKey && window.divide_buy.soft_search_base_url) {
            const iframe = document.createElement('iframe');
            iframe.src = `${window.divide_buy.soft_search_base_url}?splashKey=${splashKey}`;
            iframe.tabindex = '3';
            iframe.style.border = 'none';
            this.softCreditModalPopup?.querySelector('[data-asset="modal-popup-content"]')?.appendChild(iframe);
            this.softCreditModalPopup?.querySelector('[data-asset="modal-popup-wrapper"]')?.classList.add('active');
            const $this = this;
            iframe.addEventListener('load', function() {
                $this.softCreditModalPopup?.querySelector('[data-asset="modal-popup-content"]')?.classList.add('active');
                setTimeout(() => {
                    $this.softCreditModalPopup?.querySelector('[data-asset="modal-popup-loader"]')?.classList.add('hide');
                }, 800);
            });
        }
    }

    handleSoftSearchModalClose() {
        const content = this.softCreditModalPopup?.querySelector('[data-asset="modal-popup-content"]');
        while (content?.firstChild) {
            content.removeChild(content.firstChild);
        }
        content?.classList.remove('active');

        const wrapper = this.softCreditModalPopup?.querySelector('[data-asset="modal-popup-wrapper"]');
        wrapper?.classList.remove('active');

        this.softCreditModalPopup?.querySelector('[data-asset="modal-popup-loader"]')?.classList.remove('hide');
        this.softCreditModalPopup?.classList.remove('active');
    }

    debounce(func, wait) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    getInstalments() {
        const data = window.divide_buy.instalment_data?.sort((x, y) => x.key - y.key);
        const uniqueData = Array.from(new Set(data.map(JSON.stringify))).map(JSON.parse);
        return uniqueData;
    }

    handleTooltipModalToggle() {
        this.tooltipPopup?.classList.toggle('active');
    }

    handleTooltipModalClose() {
        this.tooltipPopup?.classList.remove('active');
    }

    calculateInstalmentAmountsForLoan(amount, interestRate, numberOfMonths) {
        if (interestRate <= 0) {
            const perMonthCost =  amount / numberOfMonths;
            return ({
                totalAmount: amount,
                interestRate: 0,
                monthlyInterestRate: 0,
                numberOfMonths,
                firstInstalment: perMonthCost,
                regularMonthlyInstalment: perMonthCost,
                totalToPay: amount,
                totalLoanValue: amount - perMonthCost,
                interest: 0,
                effectiveTerm: numberOfMonths - 1,
            })
        } else {
            const monthlyInterestRate = Math.pow((interestRate + 1) , (1 / 12)) - 1;
            const loanAmountInPence = amount * 100;
            const initialRemainder =  loanAmountInPence % numberOfMonths;
            const initialRegularInstalment = (loanAmountInPence - initialRemainder) / numberOfMonths;
            const firstInstalment = (initialRegularInstalment + initialRemainder) / 100;
            const loanPrincipal = amount - firstInstalment;
            const monthlyInstalment = (loanPrincipal * monthlyInterestRate) / (1 - Math.pow(1 + monthlyInterestRate, - (numberOfMonths - 1)));
            const totalToPay = (monthlyInstalment * (numberOfMonths - 1)) + firstInstalment;
            const interestToPay = totalToPay - amount;

            return({
                totalAmount: amount,
                interestRate,
                monthlyInterestRate,
                numberOfMonths,
                firstInstalment,
                regularMonthlyInstalment: monthlyInstalment,
                totalToPay,
                totalLoanValue: totalToPay - firstInstalment,
                interest: interestToPay,
                effectiveTerm: numberOfMonths - 1,
            })
        }
    }

    getRepresentativeAprExampleHtml(exampleAmount = 2000) {
        const representativeAprInterestRate = window.divide_buy.representative_apr;
        const numberOfMonths = Math.max(...this.instalments.filter(data => Number(data.is_ibc) === 1).map(data => data.key).filter(key => key !== undefined));
        const repAprExample = this.calculateInstalmentAmountsForLoan(exampleAmount, representativeAprInterestRate/100, numberOfMonths);

        return `<strong>Representative Example:</strong>A loan of £${parseFloat(exampleAmount).toFixed(2)} over ${numberOfMonths} months will cost you £${parseFloat(repAprExample['regularMonthlyInstalment']).toFixed(2)} per month at a representative ${parseFloat(representativeAprInterestRate).toFixed(2)}% APR. The total cost after ${numberOfMonths} months is £${parseFloat(repAprExample['totalToPay']).toFixed(2)}, which includes £${parseFloat(repAprExample['interest']).toFixed(2)} interest at ${parseFloat(representativeAprInterestRate).toFixed(2)}% fixed and a £0 fee. The total amount of credit is £${parseFloat(repAprExample['totalLoanValue']).toFixed(2)}.` ;
    }
}

customElements.define("divide-buy", DivideBuy);
